import { SiteNode } from '../types/types';

// Statuses that indicate the charger is actively delivering current
const ACTIVE_CHARGING_STATUSES = new Set([
  'Charging',
  'Preparing',
  'Finishing',
  'SuspendedEv',
  'SuspendedEvse',
]);

// Helper function to check if allocated current matches selected ranges.
// For non-active stations (offline, unavailable, etc.) always treat allocation as 0 (0-80 bucket).
function matchesAllocatedRange(allocatedCurrent: number | undefined, selectedRanges: string[], status?: string): boolean {
  const isActive = status ? ACTIVE_CHARGING_STATUSES.has(status) : true;
  const current = isActive ? (allocatedCurrent ?? 0) : 0;

  return selectedRanges.some(range => {
    if (range === '0-80') return current >= 0 && current < 80;
    if (range === '80-100') return current >= 80 && current < 100;
    if (range === '100+') return current >= 100;
    return false;
  });
}

// Helper function to filter tree data recursively
export function filterTreeData(
  nodes: SiteNode[],
  selectedStatuses: string[],
  selectedRanges: string[],
  hideInactive: boolean = false
): SiteNode[] {
  // 0 statuses or 0 ranges selected → show nothing
  if (selectedStatuses.length === 0 || selectedRanges.length === 0) {
    return [];
  }

  const filtered: SiteNode[] = [];

  for (const node of nodes) {
    // Recursively filter children first
    const filteredChildren = node.children
      ? filterTreeData(node.children, selectedStatuses, selectedRanges, hideInactive)
      : [];

    // For machine nodes, apply filters
    if (node.type === 'machine') {
      let matches = true;

      // Apply hideInactive filter first - includes both Inactive and Disconnected
      if (hideInactive && (
        node.status?.toLowerCase() === 'inactive' ||
        node.status?.toLowerCase() === 'disconnected'
      )) {
        continue; // Skip inactive and disconnected machines
      }

      // Apply charging status filter (OR logic within filter)
      // Rows with no OCPP status (no contact) pass through — they can't be filtered by status
      matches = matches && (node.status ? selectedStatuses.includes(node.status) : true);

      // Apply allocated current filter (OR logic within filter)
      matches = matches && matchesAllocatedRange(node.allocated?.current, selectedRanges, node.status);

      // Include machine if it matches filters
      if (matches) {
        filtered.push({ ...node, children: filteredChildren });
      }
    } else {
      // For non-machine nodes (actor, location, level), include if:
      // 1. They have visible children, OR
      // 2. Their own status matches the filter (when filtering by status)
      const statusMatches = selectedStatuses.length === 0 ||
                           (node.status && selectedStatuses.includes(node.status));

      if (filteredChildren.length > 0 || statusMatches) {
        filtered.push({ ...node, children: filteredChildren });
      }
    }
  }

  return filtered;
}

// Helper function to count all leaf nodes (machines) in tree
export function countLeafNodes(nodes: SiteNode[]): number {
  let count = 0;

  for (const node of nodes) {
    if (node.type === 'machine') {
      count++;
    }
    if (node.children && node.children.length > 0) {
      count += countLeafNodes(node.children);
    }
  }

  return count;
}
